/*
 * Ediots theme — core behaviours.
 *
 * Every interaction the source Next.js clone drove with React state is
 * reproduced here with plain DOM code. Nothing is bundled and nothing is
 * imported: this file is served as-is by Shopify.
 *
 *   1. AnimateIn        [data-animate-in]  -> adds [data-animate] on entry
 *   2. ParallaxImage    [data-parallax]    -> +/-20px (30 desktop) scroll drift
 *   3. Mega menu        hover-driven panels + page dimmer
 *   4. Drawers          bag / account / search clip-path wipes
 *   5. Accordion        grid-template-rows 0fr -> 1fr disclosure
 *   6. Carousel         drag + wheel + arrow scroll-snap rail
 *   7. Cart             AJAX add / change / remove against the Cart API
 *   8. TextReveal       per-word scroll-scrubbed opacity
 *   9. PageNavigation   in-page anchor spy
 */
(function () {
  'use strict';

  var DESKTOP = '(min-width: 900.02px)';
  var systemReduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var config = window.Theme || {};

  /* A visitor asking their system for reduced motion always wins; the theme
     settings can only turn motion *off* on top of that, never back on. */
  var reduceMotion = {
    get matches() {
      return systemReduceMotion.matches || config.animations === false;
    }
  };
  var parallaxOff = function () {
    return systemReduceMotion.matches || config.parallax === false;
  };

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  /* ------------------------------------------------------------------ 1 */
  /* Port of the theme's <animate-in> element. Adds `data-animate` once the
     element enters the viewport, which starts the .ani-Title_Word /
     .ani-Content / .ani-Card keyframes in theme.css. Fires once. */
  function initAnimateIn(root) {
    var nodes = $$('[data-animate-in]', root).filter(function (el) {
      return !el.hasAttribute('data-animate');
    });
    if (!nodes.length) return;

    if (!('IntersectionObserver' in window)) {
      nodes.forEach(function (el) { el.setAttribute('data-animate', ''); });
      return;
    }

    nodes.forEach(function (el) {
      // An element taller than the viewport can never reach 15% of its own
      // height on screen — that is the whole privacy policy, one ~20,000px
      // block. Clamp the ratio to what this element can actually reach.
      var threshold = 0.15;
      var height = el.offsetHeight;
      if (height > 0) {
        var reachable = (window.innerHeight / height) * 0.9;
        threshold = Math.min(threshold, reachable);
      }
      var io = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            el.setAttribute('data-animate', '');
            io.disconnect();
            return;
          }
        }
      }, { threshold: threshold });
      io.observe(el);
    });
  }

  /* ------------------------------------------------------------------ 2 */
  /* Port of <parallax-image>. The element is taller than its container and
     offset upward, then translated as it crosses the viewport:
       base        height: calc(100% + 40px); top: -20px   (+/-20px drift)
       >=900.02px  height: calc(100% + 60px); top: -30px   (+/-30px drift) */
  function initParallax() {
    var nodes = $$('[data-parallax]');
    if (!nodes.length || parallaxOff()) return;

    var frame = 0;
    function update() {
      frame = 0;
      var drift = window.matchMedia(DESKTOP).matches ? 30 : 20;
      var vh = window.innerHeight;
      nodes.forEach(function (el) {
        var parent = el.parentElement;
        if (!parent) return;
        var rect = parent.getBoundingClientRect();
        if (rect.bottom < -200 || rect.top > vh + 200) return;
        var span = vh + rect.height;
        if (span <= 0) return;
        var progress = (vh - rect.top) / span;
        var clamped = Math.min(1, Math.max(0, progress));
        el.style.transform = 'translate3d(0,' + ((clamped * 2 - 1) * drift) + 'px,0)';
      });
    }
    function onScroll() { if (!frame) frame = requestAnimationFrame(update); }

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
  }

  /* ------------------------------------------------------------------ 3 */
  /* The header mega-menu. Hover opens; an invisible bridge (the nav item's
     .hd-Banner_HoverBuffer plus the panel's own padding-inline-start) keeps the
     pointer's hover intent alive on the way across. */
  function initMegaMenu() {
    var overlay = $('[data-menu-overlay]');
    var items = $$('[data-menu-trigger]');
    var panels = $$('[data-menu-panel]');
    if (!items.length) return;

    var current = null;
    var closeTimer = 0;

    function setOpen(index) {
      if (current === index) return;
      current = index;
      panels.forEach(function (panel, i) {
        var open = i === index;
        panel.setAttribute('aria-expanded', open ? 'true' : 'false');
        panel.classList.toggle('is-open', open);
        var inner = $('[data-menu-inner]', panel);
        if (inner) inner.setAttribute('aria-hidden', open ? 'false' : 'true');
      });
      if (overlay) overlay.classList.toggle('is-open', index !== null);
      document.documentElement.classList.toggle('menu-open', index !== null);
    }

    function open(index) { clearTimeout(closeTimer); setOpen(index); }
    function scheduleClose() {
      clearTimeout(closeTimer);
      closeTimer = setTimeout(function () { setOpen(null); }, 120);
    }

    items.forEach(function (item, index) {
      item.addEventListener('mouseenter', function () { open(index); });
      item.addEventListener('mouseleave', scheduleClose);
      item.addEventListener('focusin', function () { open(index); });
    });
    panels.forEach(function (panel, index) {
      panel.addEventListener('mouseenter', function () { open(index); });
      panel.addEventListener('mouseleave', scheduleClose);
      panel.addEventListener('focusout', function (event) {
        if (!panel.contains(event.relatedTarget)) scheduleClose();
      });
    });
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') setOpen(null);
    });

    // Mobile: the hamburger promotes the same panels into a stacked drawer.
    var toggle = $('[data-mobile-menu-toggle]');
    var mobile = $('[data-mobile-menu]');
    if (toggle && mobile) {
      toggle.addEventListener('click', function () {
        var open = mobile.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        document.documentElement.classList.toggle('mobile-menu-open', open);
      });
      $$('[data-mobile-menu-close]', mobile).forEach(function (el) {
        el.addEventListener('click', function () {
          mobile.classList.remove('is-open');
          toggle.setAttribute('aria-expanded', 'false');
          document.documentElement.classList.remove('mobile-menu-open');
        });
      });
      $$('[data-mobile-accordion]', mobile).forEach(function (btn) {
        btn.addEventListener('click', function () {
          var item = btn.closest('[data-mobile-item]');
          if (!item) return;
          var open = item.getAttribute('aria-expanded') !== 'true';
          item.setAttribute('aria-expanded', open ? 'true' : 'false');
          btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        });
      });
    }
  }

  /* ------------------------------------------------------------------ 4 */
  /* .drw-Drawer — the bag / account / search panels. These do not slide in
     from the edge: they wipe open downward with a clip-path anchored under the
     announcement bar and to the right of the fixed navigation column. Escape
     closes; the backdrop is a sibling on its own z-index. */
  var Drawers = (function () {
    var openName = null;

    function panels() { return $$('[data-drawer]'); }

    function set(name) {
      openName = name;
      panels().forEach(function (panel) {
        var isOpen = panel.getAttribute('data-drawer') === name;
        panel.classList.toggle('drw-Drawer-active', isOpen);
        panel.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
      });
      var backdrop = $('[data-drawer-backdrop]');
      if (backdrop) backdrop.classList.toggle('drw-Drawers_Backdrop-active', !!name);
      document.documentElement.classList.toggle('drawer-open', !!name);
      if (name) {
        var panel = $('[data-drawer="' + name + '"]');
        var focusable = panel && $('button, [href], input, select, textarea', panel);
        if (focusable) { try { focusable.focus({ preventScroll: true }); } catch (e) { focusable.focus(); } }
      }
    }

    function init() {
      $$('[data-drawer-open]').forEach(function (btn) {
        btn.addEventListener('click', function (event) {
          event.preventDefault();
          set(btn.getAttribute('data-drawer-open'));
        });
      });
      $$('[data-drawer-close]').forEach(function (btn) {
        btn.addEventListener('click', function () { set(null); });
      });
      var backdrop = $('[data-drawer-backdrop]');
      if (backdrop) backdrop.addEventListener('click', function () { set(null); });
      document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' && openName) set(null);
      });
    }

    return { init: init, open: set, close: function () { set(null); } };
  })();

  /* ------------------------------------------------------------------ 5 */
  /* .sec-Accordion — panels open with a grid-template-rows 0fr -> 1fr
     measure-yourself transition, so the body animates to its own height with
     no JS measurement. Items are independent; a sibling is never closed. */
  function initAccordions(root) {
    $$('[data-accordion-toggle]', root).forEach(function (btn) {
      if (btn.dataset.bound) return;
      btn.dataset.bound = '1';
      btn.addEventListener('click', function () {
        var item = btn.closest('[data-accordion-item]');
        if (!item) return;
        var open = item.getAttribute('aria-expanded') !== 'true';
        item.setAttribute('aria-expanded', open ? 'true' : 'false');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });
  }

  /* ------------------------------------------------------------------ 6 */
  /* The product / stores rail. scroll-snap does the positioning; this adds the
     pointer drag, the arrow buttons and the disabled states. */
  function initCarousels(root) {
    $$('[data-carousel]', root).forEach(function (carousel) {
      if (carousel.dataset.bound) return;
      carousel.dataset.bound = '1';

      var track = $('[data-carousel-track]', carousel);
      var prev = $('[data-carousel-prev]', carousel);
      var next = $('[data-carousel-next]', carousel);
      if (!track) return;

      function step() {
        var first = track.firstElementChild;
        if (!first) return track.clientWidth;
        var styles = getComputedStyle(track);
        var gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
        return first.getBoundingClientRect().width + gap;
      }

      function sync() {
        var max = track.scrollWidth - track.clientWidth - 1;
        if (prev) prev.disabled = track.scrollLeft <= 1;
        if (next) next.disabled = track.scrollLeft >= max;
      }

      if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -step(), behavior: 'smooth' }); });
      if (next) next.addEventListener('click', function () { track.scrollBy({ left: step(), behavior: 'smooth' }); });
      track.addEventListener('scroll', function () { requestAnimationFrame(sync); }, { passive: true });
      window.addEventListener('resize', sync);
      sync();

      // pointer drag
      var down = false, startX = 0, startScroll = 0, moved = false;
      track.addEventListener('pointerdown', function (event) {
        if (event.pointerType === 'touch') return;
        down = true; moved = false;
        startX = event.clientX;
        startScroll = track.scrollLeft;
        track.classList.add('is-dragging');
      });
      track.addEventListener('pointermove', function (event) {
        if (!down) return;
        var dx = event.clientX - startX;
        if (Math.abs(dx) > 4) moved = true;
        track.scrollLeft = startScroll - dx;
      });
      function release() {
        if (!down) return;
        down = false;
        track.classList.remove('is-dragging');
      }
      track.addEventListener('pointerup', release);
      track.addEventListener('pointercancel', release);
      track.addEventListener('pointerleave', release);
      track.addEventListener('click', function (event) {
        if (moved) { event.preventDefault(); event.stopPropagation(); moved = false; }
      }, true);
    });
  }

  /* ------------------------------------------------------------------ 7 */
  var Cart = (function () {
    var routes = (window.Theme && window.Theme.routes) || {};

    function request(url, body) {
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      }).then(function (res) { return res.json(); });
    }

    function refresh() {
      return fetch(routes.root + '?sections=cart-drawer', { headers: { Accept: 'application/json' } })
        .then(function (res) { return res.json(); })
        .then(function (data) {
          var html = data['cart-drawer'];
          if (!html) return;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = $('[data-cart-contents]', doc);
          var live = $('[data-cart-contents]');
          if (fresh && live) {
            live.innerHTML = fresh.innerHTML;
            initAnimateIn(live);
            initAccordions(live);
          }
          var count = fresh && fresh.getAttribute('data-cart-count');
          if (count !== null) updateCount(parseInt(count, 10) || 0);
        })
        .catch(function () { /* the drawer simply keeps its last render */ });
    }

    function updateCount(count) {
      $$('[data-cart-count-dot]').forEach(function (dot) {
        dot.classList.toggle('scale-100', count > 0);
        dot.classList.toggle('scale-0', count === 0);
      });
      $$('[data-cart-count-text]').forEach(function (el) { el.textContent = count; });
    }

    function add(id, quantity, properties) {
      var payload = { items: [{ id: id, quantity: quantity || 1 }] };
      if (properties) payload.items[0].properties = properties;
      return request(routes.cartAdd, payload).then(function (result) {
        if (result.status) return Promise.reject(result);
        return refresh().then(function () {
          if (config.openCartOnAdd !== false) Drawers.open('cart');
        });
      });
    }

    function change(key, quantity) {
      return request(routes.cartChange, { id: key, quantity: quantity }).then(refresh);
    }

    function init() {
      document.addEventListener('submit', function (event) {
        var form = event.target.closest('[data-product-form]');
        if (!form) return;
        event.preventDefault();
        var button = $('[data-add-to-cart]', form);
        if (button) button.setAttribute('disabled', 'disabled');
        var data = new FormData(form);
        fetch(routes.cartAdd, { method: 'POST', body: data, headers: { Accept: 'application/json' } })
          .then(function (res) { return res.json(); })
          .then(function (result) {
            if (result.status) throw result;
            return refresh().then(function () {
              if (config.openCartOnAdd !== false) Drawers.open('cart');
            });
          })
          .catch(function (error) {
            var msg = $('[data-form-error]', form);
            if (msg) msg.textContent = (error && error.description) || 'Sorry, that could not be added.';
          })
          .then(function () { if (button) button.removeAttribute('disabled'); });
      });

      document.addEventListener('click', function (event) {
        var el = event.target.closest('[data-cart-change]');
        if (!el) return;
        event.preventDefault();
        change(el.getAttribute('data-cart-key'), parseInt(el.getAttribute('data-cart-change'), 10));
      });
    }

    return { init: init, add: add, change: change, refresh: refresh };
  })();

  /* ------------------------------------------------------------------ 8 */
  /* `.sec-TextReveal` — each word span is clipped to --Reveal_Amount of its own
     width, so raising that from 0% to 100% wipes the bright word across the dim
     base copy; running the words in sequence reads as a left-to-right reveal.
     The band is 150vh tall and its body sticks for the whole of it, so the
     extra 50vh of scroll is what gets scrubbed. */
  function initTextReveal() {
    var blocks = $$('[data-text-reveal]');
    if (!blocks.length) return;

    if (reduceMotion.matches) {
      blocks.forEach(function (block) {
        $$('[data-reveal-word]', block).forEach(function (w) {
          w.style.setProperty('--Reveal_Amount', '100%');
        });
      });
      return;
    }

    var frame = 0;
    function update() {
      frame = 0;
      blocks.forEach(function (block) {
        var words = $$('[data-reveal-word]', block);
        if (!words.length) return;
        var rect = block.getBoundingClientRect();
        var scrubbable = rect.height - window.innerHeight;
        // A band shorter than the viewport has nothing to scrub: show it all.
        var progress = scrubbable <= 0 ? 1 : Math.min(1, Math.max(0, -rect.top / scrubbable));
        words.forEach(function (word, index) {
          var amount = Math.min(1, Math.max(0, progress * words.length - index)) * 100;
          word.style.setProperty('--Reveal_Amount', amount + '%');
        });
      });
    }
    function onScroll() { if (!frame) frame = requestAnimationFrame(update); }
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
  }

  /* ------------------------------------------------------------------ 9 */
  function initPageNavigation() {
    var navs = $$('[data-page-nav]');
    if (!navs.length) return;
    navs.forEach(function (nav) {
      var links = $$('a[href^="#"]', nav);
      var targets = links.map(function (link) { return document.getElementById(link.getAttribute('href').slice(1)); });
      links.forEach(function (link) {
        link.addEventListener('click', function (event) {
          var target = document.getElementById(link.getAttribute('href').slice(1));
          if (!target) return;
          event.preventDefault();
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      });
      if (!('IntersectionObserver' in window)) return;
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var index = targets.indexOf(entry.target);
          links.forEach(function (link, i) { link.setAttribute('aria-current', i === index ? 'true' : 'false'); });
        });
      }, { rootMargin: '-40% 0px -55% 0px' });
      targets.forEach(function (target) { if (target) io.observe(target); });
    });
  }

  /* ------------------------------------------------------------------ 6b */
  /* `.sec-ProductCarousel` — the source theme drives this with keen-slider
     inside a `product-carousel-tabs` custom element, but that element only ever
     renders a single panel with one title, so the tab machinery is degenerate
     and is not reproduced. Movement is a plain transform track advanced one
     slide per click; 1 / 2 / 3 slides are visible at base / 768px / 1200.02px
     and the viewport is 90% wide so the next slide peeks in at the right edge. */
  function initProductCarousels(root) {
    $$('[data-product-carousel]', root).forEach(function (carousel) {
      if (carousel.dataset.bound) return;
      carousel.dataset.bound = '1';

      var slides = $('[data-pc-slides]', carousel);
      var prev = $('[data-pc-prev]', carousel);
      var next = $('[data-pc-next]', carousel);
      if (!slides) return;

      var count = slides.children.length;
      var index = 0;

      function perView() {
        if (window.matchMedia('(min-width: 1200.02px)').matches) return 3;
        if (window.matchMedia('(min-width: 768px)').matches) return 2;
        return 1;
      }

      /* Below 900.02px the rail is a native scroll container and the arrows are
       * hidden. A transform there would fight the shopper's own finger — the
       * track would be shifted by the script and scrolled by the touch at the
       * same time — so the script keeps its hands off and lets the browser do
       * it, which is also what gives real momentum and rubber-banding. */
      function isTouchRail() {
        return !window.matchMedia('(min-width: 900.02px)').matches;
      }

      function render() {
        if (isTouchRail()) {
          /* Clear anything a wider layout left behind, or the rail would start
             scrolled sideways after a resize or an orientation change. */
          slides.style.transform = '';
          slides.style.transition = '';
          index = 0;
          return;
        }

        var spv = perView();
        var max = Math.max(0, count - spv);
        if (index > max) index = max;
        var width = 100 / spv;
        slides.style.transform = 'translate3d(-' + (index * width) + '%,0,0)';
        slides.style.transition = reduceMotion.matches ? 'none' : 'transform .5s ease';
        if (prev) prev.disabled = index === 0;
        if (next) next.disabled = index >= max;
      }

      if (prev) prev.addEventListener('click', function () { index = Math.max(0, index - 1); render(); });
      if (next) next.addEventListener('click', function () {
        index = Math.min(Math.max(0, count - perView()), index + 1);
        render();
      });
      window.addEventListener('resize', render);
      render();
    });
  }

  /* ----------------------------------------------------------------- 10 */
  /* `.ft-Background` — the sticky brand panel behind the footer. The overlay
     alpha is driven by the footer's own scroll position and written onto the
     .ft-Footer element as --Overlay, which the overlay layer reads. */
  var FOOTER_MAX_OVERLAY = typeof config.footerOverlay === 'number' ? config.footerOverlay : 0.6;
  var FOOTER_REDUCED_OVERLAY = FOOTER_MAX_OVERLAY / 2;

  function initFooterBackdrop() {
    var footer = $('[data-footer]');
    if (!footer) return;
    if (reduceMotion.matches) {
      footer.style.setProperty('--Overlay', String(FOOTER_REDUCED_OVERLAY));
      return;
    }
    var frame = 0;
    function update() {
      frame = 0;
      var rect = footer.getBoundingClientRect();
      var scrollable = rect.height - window.innerHeight;
      var progress = scrollable <= 0 ? 1 : Math.min(1, Math.max(0, -rect.top / scrollable));
      footer.style.setProperty('--Overlay', String(progress * FOOTER_MAX_OVERLAY));
    }
    function onScroll() { if (!frame) frame = requestAnimationFrame(update); }
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
  }

  /* ----------------------------------------------------------------- 11 */
  /* Live HH:MM:SS clock for the brand's home timezone. Renders empty until the
     first tick so the server markup and the first client paint agree. */
  function initClock() {
    $$('[data-clock]').forEach(function (el) {
      var zone = el.getAttribute('data-timezone') || 'America/New_York';
      var format;
      try {
        format = new Intl.DateTimeFormat('en-GB', {
          timeZone: zone, hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
        });
      } catch (e) { return; }
      function tick() { el.textContent = format.format(new Date()); }
      tick();
      setInterval(tick, 1000);
    });
  }

  /* ----------------------------------------------------------------- 12 */
  /* Account drawer tabs. The dot the theme parks on the active tab is the
     `.drw-Account_TabDot`; the inactive tab drops to 30% opacity. */
  function initAccountTabs() {
    var buttons = $$('[data-account-tab]');
    if (!buttons.length) return;
    function select(name) {
      $$('[data-account-panel]').forEach(function (panel) {
        panel.hidden = panel.getAttribute('data-account-panel') !== name;
      });
      buttons.forEach(function (btn) {
        if (!btn.classList.contains('drw-Account_Tab')) return;
        var active = btn.getAttribute('data-account-tab') === name;
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }
    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () { select(btn.getAttribute('data-account-tab')); });
    });
    select('login');
  }

  /* ----------------------------------------------------------------- 13 */
  function initLocaleSwitcher() {
    var toggle = $('[data-locale-toggle]');
    var list = $('[data-locale-list]');
    if (!toggle || !list) return;
    toggle.addEventListener('click', function () {
      var open = list.classList.toggle('hidden');
      toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
    });
  }

  /* Background video on the banner sections.
   *
   * The markup already carries `autoplay loop muted`, so this is not what makes
   * the video play — it is what makes it play *correctly*:
   *
   *   * `playsInline` is set here because iOS otherwise refuses to autoplay a
   *     video inline and takes it fullscreen instead, and Shopify's `video_tag`
   *     does not promise the attribute.
   *   * `muted` is re-asserted because a browser will silently decline to
   *     autoplay anything with sound, and the attribute alone is not always
   *     enough once a script has touched the element.
   *   * A visitor who has asked their system for reduced motion, or a merchant
   *     who has switched animations off, gets a still. Hiding the video in CSS
   *     would not be enough — it would keep downloading and playing behind the
   *     poster.
   *   * Off-screen videos are paused. A looping video decoding at the bottom of
   *     a long page costs battery for nothing.
   */
  function initBackgroundVideo(scope) {
    var wraps = $$('[data-background-video]', scope || document);
    if (!wraps.length) return;

    var reduced = window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var allowed = config.animations !== false && !reduced;

    wraps.forEach(function (wrap) {
      var video = wrap.querySelector('video');
      if (!video || video.dataset.videoReady) return;
      video.dataset.videoReady = 'true';

      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.muted = true;
      video.setAttribute('tabindex', '-1');

      if (!allowed) {
        video.removeAttribute('autoplay');
        video.pause();
        wrap.setAttribute('data-video-paused', 'true');
        return;
      }

      function play() {
        var attempt = video.play();
        /* Autoplay can still be refused — a data-saver mode, a battery-saver
         * mode, a browser setting. The poster stays up; nothing to report. */
        if (attempt && attempt.catch) attempt.catch(function () {});
      }

      if (!window.IntersectionObserver) { play(); return; }

      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) play();
          else video.pause();
        });
      }, { rootMargin: '200px' }).observe(video);
    });
  }

  /* Predictive search.
   *
   * The form under it is a real GET to the search page, so this is an
   * accelerator and nothing more: every failure mode here — no fetch, a dropped
   * request, a slow network — leaves the shopper with a working search box.
   *
   * Requests are debounced and the previous one aborted, because a shopper
   * typing "sneakers" would otherwise fire eight queries and race their answers
   * back in whatever order the network felt like. */
  function initPredictiveSearch() {
    var form = document.querySelector('[data-predictive-search]');
    if (!form || form.hasAttribute('data-predictive-off')) return;
    if (typeof fetch !== 'function') return;

    var input = form.querySelector('[data-predictive-input]');
    var results = form.querySelector('[data-predictive-results]');
    var endpoint = form.getAttribute('data-endpoint');
    var types = form.getAttribute('data-types') || 'product';
    if (!input || !results || !endpoint) return;

    var timer = null;
    var controller = null;

    function clear() {
      results.innerHTML = '';
      input.setAttribute('aria-expanded', 'false');
    }

    function query(term) {
      if (controller) controller.abort();
      controller = typeof AbortController === 'function' ? new AbortController() : null;

      var params = 'q=' + encodeURIComponent(term) +
        '&resources[type]=' + encodeURIComponent(types) +
        '&resources[limit]=6' +
        '&section_id=predictive-search';

      fetch(endpoint + '?' + params, controller ? { signal: controller.signal } : undefined)
        .then(function (response) {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then(function (html) {
          /* The response is the section wrapped in Shopify's own div; taking
             its inner HTML avoids nesting a second wrapper every keystroke. */
          var holder = document.createElement('div');
          holder.innerHTML = html;
          var inner = holder.querySelector('.shopify-section') || holder;
          results.innerHTML = inner.innerHTML;
          input.setAttribute('aria-expanded', results.innerHTML.trim() ? 'true' : 'false');
        })
        .catch(function () { /* the form still works */ });
    }

    input.addEventListener('input', function () {
      var term = input.value.trim();
      window.clearTimeout(timer);
      if (term.length < 2) { clear(); return; }
      timer = window.setTimeout(function () { query(term); }, 250);
    });

    input.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      /* Escape clears the suggestions first and only closes the drawer if there
         are none — otherwise one key does two things at once. */
      if (results.innerHTML.trim()) {
        event.stopPropagation();
        clear();
      }
    });
  }

  /* Product recommendations.
   *
   * Shopify fills the `recommendations` object only when the section is
   * requested through this endpoint, so the server-rendered shell is empty by
   * design and this replaces it once. If the request fails or comes back with
   * nothing, the shell stays empty and the page simply has no rail — no error
   * state is worth showing for a suggestion the shopper never asked for. */
  function initRecommendations() {
    var shell = document.querySelector('[data-product-recommendations]');
    if (!shell || typeof fetch !== 'function' || typeof DOMParser !== 'function') return;

    var url = shell.getAttribute('data-url');
    var intent = shell.getAttribute('data-intent');
    if (!url) return;
    if (intent) url += '&intent=' + encodeURIComponent(intent);

    fetch(url)
      .then(function (response) {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var fresh = parsed.querySelector('[data-product-recommendations]');
        if (!fresh || !fresh.innerHTML.trim()) return;
        shell.innerHTML = fresh.innerHTML;
        initProductCarousels(shell);
        initAnimateIn(shell);
      })
      .catch(function () { /* no rail */ });
  }

  /* `.chp-Chapters` — a centre-snapping rail where the copy underneath belongs
   * to whichever slide is centred.
   *
   * The scrolling, the snapping and the drag are all native; this adds only the
   * part CSS cannot answer — which slide is in the middle right now. So the
   * section degrades to a plain readable slider if this never runs, and
   * `data-js` is set here rather than in the markup so the dimmed-neighbour
   * treatment only ever applies when something is around to undim them.
   *
   * Position is measured against the rail's own centre rather than the
   * viewport's, because the rail is not always full width and a section sitting
   * off to one side would otherwise decide its first slide is permanently
   * active. */
  function initChapterCarousels(root) {
    $$('[data-chapter-carousel]', root).forEach(function (shell) {
      if (shell.dataset.bound) return;
      shell.dataset.bound = '1';

      var track = $('[data-chapter-track]', shell);
      var slides = $$('[data-chapter-slide]', shell);
      var captions = $$('[data-chapter-caption]', shell);
      var dots = $$('[data-chapter-dot]', shell);
      if (!track || !slides.length) return;

      shell.setAttribute('data-js', '');
      var current = -1;

      function activate(index) {
        if (index === current) return;
        current = index;
        slides.forEach(function (slide, i) { slide.classList.toggle('is-active', i === index); });
        captions.forEach(function (caption, i) { caption.hidden = i !== index; });
        dots.forEach(function (dot, i) {
          if (i === index) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
      }

      function measure() {
        var railBox = track.getBoundingClientRect();
        var centre = railBox.left + railBox.width / 2;
        var best = 0;
        var bestDistance = Infinity;
        slides.forEach(function (slide, i) {
          var box = slide.getBoundingClientRect();
          var distance = Math.abs(box.left + box.width / 2 - centre);
          if (distance < bestDistance) { bestDistance = distance; best = i; }
        });
        activate(best);
      }

      var ticking = false;
      track.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () { ticking = false; measure(); });
      }, { passive: true });
      window.addEventListener('resize', measure);

      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () {
          var slide = slides[i];
          if (!slide) return;
          // scrollIntoView would scroll the PAGE to reach the rail as well;
          // setting scrollLeft moves only the rail, which is what a dot means.
          var offset = slide.offsetLeft - (track.clientWidth - slide.offsetWidth) / 2;
          track.scrollTo({ left: offset, behavior: 'smooth' });
        });
      });

      measure();
    });
  }

  /* `.evt-Event[data-scroll="pinned"]` — the event gallery driven by the page.
   *
   * The section sticks to the viewport for a fixed run of scrolling, and that
   * scroll progress is mapped onto a horizontal offset for the rail. The big
   * picture gets a fraction of the same offset, which is what reads as depth:
   * two things moving at different speeds is the whole of parallax.
   *
   * Everything here is opt-in and reversible. `data-js` is only set once this
   * has measured a real distance to travel, and the stylesheet keeps the
   * ordinary dragged rail until it appears — so a browser that never runs this,
   * or one whose owner asked for reduced motion, gets a section that still
   * works rather than a tall empty pin it cannot scroll past.
   */
  function initEventGalleries(root) {
    var galleries = $$('[data-event-gallery]', root).filter(function (el) {
      return el.dataset.scroll === 'pinned' && !el.dataset.bound;
    });
    if (!galleries.length) return;

    var desktop = window.matchMedia(DESKTOP);

    galleries.forEach(function (shell) {
      shell.dataset.bound = '1';

      var pin = $('.evt-Event_Pin', shell);
      var stage = $('.evt-Event_Stage', shell);
      var track = $('[data-carousel-track]', shell);
      var panel = $('.evt-Event_Panel', shell);
      var body = $('.evt-Event_PanelBody', shell);
      if (!pin || !stage || !track) return;

      var distance = 0;
      var frame = 0;

      function measure() {
        // How far the rail has to travel: everything past the right-hand edge
        // of the stage. Measured from the live layout rather than assumed from
        // the block count, because the card width is a percentage and the
        // merchant can change it.
        var overhang = track.scrollWidth - stage.clientWidth;
        distance = Math.max(0, overhang);
        if (!distance || !desktop.matches || reduceMotion.matches) {
          shell.removeAttribute('data-js');
          shell.style.removeProperty('--Rail_Shift');
          shell.style.removeProperty('--Panel_Shift');
          shell.style.removeProperty('--Title_Fade');
        } else {
          shell.setAttribute('data-js', '');
        }
      }

      function update() {
        frame = 0;
        if (!shell.hasAttribute('data-js')) return;

        var box = pin.getBoundingClientRect();
        // Progress through the pinned run: 0 as the stage reaches the top of
        // the viewport, 1 as the pin's last screen leaves it.
        var travel = box.height - window.innerHeight;
        if (travel <= 0) return;
        var progress = Math.min(1, Math.max(0, -box.top / travel));

        var drift = (parseFloat(getComputedStyle(shell).getPropertyValue('--Panel_Drift')) || 0) / 100;
        var railShift = progress * distance;

        // The picture drifts, but it never leaves. Capped at 40% of its own
        // width, so the far end of the scroll still has an establishing shot on
        // the left instead of an empty margin — and so the cap is a share of
        // the picture rather than a pixel count that would mean something
        // different at every screen size and every rail length.
        var panelShift = railShift * drift;
        if (panel) panelShift = Math.min(panelShift, panel.offsetWidth * 0.4);

        shell.style.setProperty('--Rail_Shift', railShift.toFixed(2));
        shell.style.setProperty('--Panel_Shift', panelShift.toFixed(2));
        // The title dims over the first third and holds, so it is gone by the
        // time the photographs are the only thing worth looking at.
        shell.style.setProperty('--Title_Fade', Math.max(0.15, 1 - progress * 2.6).toFixed(3));
      }

      function onScroll() { if (!frame) frame = requestAnimationFrame(update); }

      measure();
      update();
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', function () { measure(); update(); });
      if (desktop.addEventListener) {
        desktop.addEventListener('change', function () { measure(); update(); });
      }
      // Photographs arriving late change the track's width, and the distance
      // was measured before they did.
      $$('img', track).forEach(function (img) {
        if (!img.complete) img.addEventListener('load', function () { measure(); update(); }, { once: true });
      });
    });
  }

  /* ----------------------------------------------------------------- go */
  function boot() {
    initAnimateIn(document);
    initParallax();
    initBackgroundVideo(document);
    initMegaMenu();
    Drawers.init();
    initAccordions(document);
    initCarousels(document);
    initProductCarousels(document);
    initChapterCarousels(document);
    initEventGalleries(document);
    Cart.init();
    initTextReveal();
    initPageNavigation();
    initFooterBackdrop();
    initClock();
    initAccountTabs();
    initLocaleSwitcher();
    initPredictiveSearch();
    initRecommendations();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Re-run the behaviours that attach to markup, over a subtree that has just
   * been swapped in. Anything that binds to `document` or to the shell (the
   * drawers, the cart, the clock) is deliberately left alone: it is still
   * attached, and re-initialising it would double up its listeners. */
  function refresh(scope) {
    var root = scope || document;
    initAnimateIn(root);
    initAccordions(root);
    initCarousels(root);
    initProductCarousels(root);
    initChapterCarousels(root);
    initEventGalleries(root);
    initBackgroundVideo(root);
  }

  // Theme editor: re-run for whatever section was just re-rendered.
  document.addEventListener('shopify:section:load', function (event) {
    refresh(event.target);
  });

  window.Theme = window.Theme || {};
  window.Theme.refresh = refresh;
  window.Theme.Cart = Cart;
  window.Theme.Drawers = Drawers;
  window.Theme.initAnimateIn = initAnimateIn;
  window.Theme.initCarousels = initCarousels;
  window.Theme.initProductCarousels = initProductCarousels;
  window.Theme.initChapterCarousels = initChapterCarousels;
  window.Theme.initEventGalleries = initEventGalleries;
  window.Theme.initAccordions = initAccordions;
})();
